import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { OrganizationProfileSettingsDto, ResponseDto } from "@mimber/schema"
import { organizationKeys } from "../organization.utils"

interface DeleteOrganizationLogoOptions extends Omit<
  UseMutationOptions<ResponseDto<OrganizationProfileSettingsDto>, Error, void>,
  "mutationFn"
> {}

/**
 * Hook for deleting organization logo
 *
 * Removes the logo file from S3 and clears the organization's logo storage item.
 *
 * @param uuid - Organization UUID
 * @param options - Additional options for the mutation
 * @returns Mutation hook for deleting logo
 *
 * @example
 * ```tsx
 * const deleteLogo = useDeleteOrganizationLogo(organizationUuid, {
 *   onSuccess: () => {
 *     toast.success('Logo deleted successfully')
 *   }
 * })
 *
 * // In delete button onClick handler
 * const handleDelete = () => {
 *   if (confirm('Are you sure you want to delete the logo?')) {
 *     deleteLogo.mutate()
 *   }
 * }
 * ```
 */
export const useDeleteOrganizationLogo = (
  uuid: string,
  options: DeleteOrganizationLogoOptions = {}
): UseMutationResult<
  ResponseDto<OrganizationProfileSettingsDto>,
  Error,
  void
> => {
  guardAppUsage("mimber-organization", "useDeleteOrganizationLogo")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async () => {
      const response = await server.delete<
        ResponseDto<OrganizationProfileSettingsDto>
      >(`organizations/${uuid}/logo`)
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      // Invalidate current organization query to refetch without logo storage item
      queryClient.invalidateQueries({
        queryKey: organizationKeys.getCurrentOrganization(),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
